<template>
  <div>
    <v-toolbar
      class="mt-4 custom-toolbar"
      elevation="0"
    >
      <v-text-field
        v-if="!isEmpty"
        clearable
        hide-details
        prepend-inner-icon="mdi-magnify"
        :label="$t('commons.search')"
        v-model="search"
      />
    </v-toolbar>

    <!-- DATATABLE -->
    <v-data-table
      class="ma-10"
      :value="selected"
      :headers="headers"
      item-key="key"
      :items="items"
      :items-per-page="-1"
      :loading="isLoading"
      :search="search"
      :show-select="mode === 'report'"
      :single-select="false"
      dense
      disable-pagination
      hide-default-footer
      @item-selected="onSelectChange"
      @toggle-select-all="onSelectAll"
    >
      <!-- Case table is loading -->
      <template #loading>
        <div class="placeholder">
          <span class="text-h6 font-weight-light">
            {{ $t("views.documentation.loading_files") }}
          </span>
        </div>
      </template>

      <template #no-data>
        <div class="placeholder">
          <!-- Folder is undefined -->
          <div v-if="isUndefined">
            <v-icon
              size="56"
              color="grey"
              >mdi-folder
            </v-icon>
            <div class="text-h6 font-weight-light">
              {{ $t("views.documentation.select_folder") }}
            </div>
          </div>

          <!-- Folder is empty -->
          <div v-else>
            <v-icon
              size="56"
              color="grey"
              >mdi-folder-open
            </v-icon>
            <div class="text-h6 font-weight-light">
              {{ $t("views.documentation.folder_empty", { name: value.name }) }}
            </div>

            <!-- Handling upload button -->
            <v-btn
              v-if="!isLocked"
              @click="openUpload"
              class="mt-2"
              color="secondary"
              outlined
              small
            >
              <v-icon left>mdi-download</v-icon>
              {{ $t("actions.upload", { entity: $tc("commons.file", 1).toLowerCase() }) }}
            </v-btn>
          </div>
        </div>
      </template>

      <template #item.name="{ item }">
        <div class="text-truncate">
          <v-icon medium>{{ getFileIcon(item) }}</v-icon>
          {{ item.name }}
          <v-icon
            v-if="isOngoingNC(item)"
            small
            color="error"
            >mdi-record
          </v-icon>
        </div>
      </template>

      <template v-slot:item.tags="{ item }">
        <div class="d-flex justify-center align-center">
          <div
            v-for="(tag, idx) in item.tags"
            :key="idx"
          >
            <avs-chip
              :label="false"
              styleC=" width: fit-content;"
              class="mr-1"
              v-if="tag"
            >
              {{ tag }}
            </avs-chip>
          </div>
        </div>
      </template>

      <template #item.date="{ item }">
        {{ getFileDatetime(item) }}
      </template>

      <template #item.actions="{ item }">
        <div class="d-flex justify-end">
          <avs-editButton
            v-if="isUsableByViewer(item) && !isLocked"
            @click.stop="openRename(item)"
            :spantext="$t('actions.edit', { entity: $tc('commons.file', 1).toLowerCase() })"
          />
          <v-btn
            icon
            :color="getExpireButtonColor(item)"
            fab
            small
            v-if="isUsableByViewer(item) && !isLocked"
            :disabled="!isUsableByViewer(item)"
            @click.stop="openExpire(item)"
            :spantext="$t('actions.edit', { entity: $tc('commons.folder', 1).toLowerCase() })"
          >
            <v-icon>mdi-calendar</v-icon>
          </v-btn>
          <v-btn
            icon
            :color="getExpireButtonColor(item)"
            fab
            small
            v-if="isPDF(item) && !isLocked"
            :disabled="!isPDF(item)"
            @click.stop="analyze_pdf(item)"
            :spantext="$t('actions.edit', { entity: $tc('commons.folder', 1).toLowerCase() })"
          >
            <v-icon>mdi-file-search-outline</v-icon>
          </v-btn>
          <avs-button
            icon
            color="secondary"
            :iconI="!isUsableByViewer(item) ? 'mdi-eye-off' : 'mdi-eye'"
            label
            fab
            small
            @click="openViewer(item)"
            :disabled="!isUsableByViewer(item)"
            :spantext="$t('actions.open', { entity: $tc('commons.file', 1).toLowerCase() })"
          />

          <avs-button
            v-if="canGetImage"
            icon
            color="secondary"
            iconI="mdi-brain"
            label
            fab
            small
            @click.stop="openAI(item)"
            :disabled="!isUsableByAI(item)"
            :spantext="$t('actions.analyze_ai')"
          />

          <v-menu
            offset-y
            v-if="!isUsableByAI(item) && !loading && !isVideoorAudio(item)"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                v-on="on"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item>
                <v-list-item-content>
                  <avs-downloadButton
                    v-if="!loading"
                    @click.stop="openDownload(item)"
                    :iconName="pdfIcon"
                    :spantext="
                      $t('actions.download', { entity: $tc('commons.file', 1).toLowerCase() })
                    "
                  />
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item>
                <v-list-item-content>
                  <v-icon v-if="item.file_type == ''">mdi-file-word-outline </v-icon>
                  <template v-else>
                    <avs-downloadButton
                      v-if="!loading"
                      @click.stop="openDownloadWord(item)"
                      :iconName="wordIcon"
                      :spantext="
                        $t('actions.download', { entity: $tc('commons.file', 1).toLowerCase() })
                      "
                    />
                  </template>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
          <avs-downloadButton
            v-if="!loading && (isUsableByAI(item) || isVideoorAudio(item))"
            @click.stop="openDownload(item)"
            :spantext="$t('actions.download', { entity: $tc('commons.file', 1).toLowerCase() })"
          />

          <avs-button
            v-if="loading"
            icon
            color="secondary"
            class="spin-loading"
            :iconI="loading ? 'mdi-loading spin' : ''"
            label
            fab
            small
            :spantext="$t('actions.open', { entity: $tc('commons.file', 1).toLowerCase() })"
          />

          <avs-deleteButton
            v-if="isDeletable(item)"
            @click.stop="openDelete(item)"
            :spantext="$t('actions.delete', { entity: $tc('commons.file', 1).toLowerCase() })"
          />
        </div>
      </template>
    </v-data-table>

    <!-- DIALOG -->
    <component
      v-model="isDialogOpen"
      :is="dialog"
      :item="editedItem"
      :title="$t('commons.rename')"
      @close="closeDialog"
    />
    <component
      v-model="isDialogOpenExpire"
      :is="dialog"
      :item="editedItem"
      :title="$t('commons.date.expire_date')"
      @close="closeDialog"
    />
  </div>
</template>

<script>
import DialogDeleteFile from "./DialogDeleteFile.vue"
import EditDocumentDialog from "./EditDocumentDialog.vue"
import EditDateDocumentDialog from "./EditDateDocumentDialog.vue"
import DialogIA from "./DialogIA.vue"
import DialogImgViewer from "./DialogImgViewer.vue"
import DialogPdfViewer from "./DialogPdfViewer.vue"

import DocumentationService from "@/services/DocumentationService"
import PcqDsiService from "@/services/PcqDsiService"
import { toDatetime } from "@/utils/datetime"
import { downloadRaw, getFilename } from "@/utils/download"

const ICONS = {
  zip: "mdi-folder-zip-outline",
  rar: "mdi-folder-zip-outline",
  htm: "mdi-language-html5",
  html: "mdi-language-html5",
  js: "mdi-nodejs",
  json: "mdi-json",
  md: "mdi-markdown",
  png: "mdi-file-image",
  jpg: "mdi-file-image",
  jpeg: "mdi-file-image",
  mp4: "mdi-filmstrip",
  mkv: "mdi-filmstrip",
  avi: "mdi-filmstrip",
  wmv: "mdi-filmstrip",
  mov: "mdi-filmstrip",
  record: "mdi-video",
  txt: "mdi-file-document-outline",
  xls: "mdi-file-excel",
  xlsx: "mdi-file-excel",
  xlsm: "mdi-file-excel",
  xlsb: "mdi-file-excel",
  csv: "mdi-file-excel",
  docx: "mdi-file-word",
  file: "mdi-file-outline",
  pdf: "mdi-file-pdf-box"
}

const HEADERS = {
  report: [
    { text: "Files", value: "name", width: "70%" },
    { text: "Date", value: "date", width: "30%" }
  ],
  nc_header: [
    { text: "Files", value: "name", align: "left" },
    { text: "N° NC", value: "nc_number", align: "left" },
    { text: "Tags", value: "tags", align: "center" },
    { text: "Date", value: "date", align: "right" },
    { text: "Actions", value: "actions", sortable: false, align: "right" }
  ],
  inspection_header: [
    { text: "Inspection", value: "name", align: "left" },
    { text: "Form", value: "document_name", align: "left" },
    { text: "Tags", value: "tags", align: "center" },
    { text: "Date", value: "date", align: "right" },
    { text: "Actions", value: "actions", sortable: false, align: "right" }
  ],
  pcq_header: [
    { text: "Files", value: "name", align: "left" },
    { text: "Revision", value: "revision", align: "left" },
    { text: "Tags", value: "tags", align: "center" },
    { text: "Date", value: "date", align: "right" },
    { text: "Actions", value: "actions", sortable: false, align: "right" }
  ],
  standard: [
    { text: "Files", value: "name", width: "20%", align: "left" },
    { text: "Tags", value: "tags", width: "30%", align: "center" },
    { text: "Date", value: "date", width: "20%", align: "right" },
    { text: "Actions", value: "actions", sortable: false, align: "right", width: "30%" }
  ]
}

const MODES = Object.keys(HEADERS)

export default {
  name: "FolderContent",
  components: {
    DialogDeleteFile,
    DialogImgViewer,
    DialogPdfViewer,
    DialogIA,
    EditDocumentDialog,
    EditDateDocumentDialog
  },
  props: {
    value: {
      type: Object
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: "standard",
      validation: v => MODES.includes(v)
    },
    selected: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    items() {
      return this.value?.files || []
    },
    isEmpty() {
      return this.items.length === 0
    },
    isLocked() {
      return !this.value.id
    },
    isUndefined() {
      return this.value == null
    },
    headers() {
      if (this.value?.key == "ncs'") {
        return HEADERS["nc_header"]
      }

      if (this.value?.key == "inspections'") {
        return HEADERS["inspection_header"]
      }

      if (this.value?.key == "pcq_dsis'") {
        return HEADERS["pcq_header"]
      }

      return HEADERS[this.mode]
    },
    projectId() {
      return this.$store.state.app.project.id
    },
    canGetImage() {
      return this.value?.key == "medias'" || !this.isLocked
    }
  },
  data() {
    return {
      dialog: null,
      editedItem: null,
      isDialogOpen: false,
      isDialogOpenExpire: false,
      search: "",
      loading: false,
      pdfIcon: "mdi-file-pdf-box",
      wordIcon: "mdi-file-word-box"
    }
  },
  methods: {
    analyze_pdf(item) {
      const document = {
        file_url: item.file_url,
        filename: `analyzed-${item.name}`
      }

      DocumentationService.analyze_pdf(this, this.projectId, document).then(res => {
        // Charger le PDF modifié dans le viewer
        this.editedItem = {
          ...item,
          file_url: res.file_url
        }
        this.dialog = "DialogPdfViewer"
        this.isDialogOpen = true

        this.$store.commit("app/message", {
          msg: `Analyse IA terminée – Fraude : ${res.isFraud ? "Oui" : "Non"} | Motif : ${
            res.reason
          }`,
          color: res.isFraud ? "error" : "success"
        })
      })
    },
    closeDialog(hasChanged) {
      if (hasChanged) this.$emit("hasChanged")
      this.dialog = null
      this.editedItem = null
      this.isDialogOpen = false
      this.isDialogOpenExpire = false
    },
    isDeletable(item) {
      return !!item.file_url
    },
    getExpireButtonColor(item) {
      if (!item.expire_date) {
        return "secondary"
      }

      const now = new Date()
      const expirationDate = new Date(item.expire_date)
      const diffTime = expirationDate - now
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      if (diffDays <= 14) {
        return "error" // Rouge si expiration dans 2 semaines ou moins
      } else if (diffDays <= 30) {
        return "orange" // Orange si expiration entre 2 semaines et 1 mois
      } else {
        return "success" // Vert si expiration dans plus de 1 mois
      }
    },
    isPDF(item) {
      return this.getFileExt(item.file_url) === "pdf"
    },
    isWord(item) {
      return this.getFileExt(item.file_url) === "docx"
    },
    isOngoingNC(item) {
      return item.file_type == "non_conformity" && item.status == "Ongoing"
    },
    isUsableByAI(item) {
      const allowed = ["png", "jpeg", "jpg"]
      const extension = this.getFileExt(item.file_url)
      return allowed.includes(extension)
    },
    isVideoorAudio(item) {
      const allowed = ["audio", "video"]
      const extension = item.file_type
      return allowed.includes(extension)
    },
    isUsableByViewer(item) {
      const allowed = ["png", "jpeg", "jpg", "pdf"]
      const extension = this.getFileExt(item.file_url)
      return allowed.includes(extension)
    },
    getFileExt(rawURL) {
      if (rawURL) {
        const pathname = new URL(rawURL).pathname
        return pathname.split(".").reverse()[0].toLowerCase()
      } else {
        return "file"
      }
    },
    getFileIcon(item) {
      return ICONS[this.getFileExt(item.file_url)]
    },
    getFileDatetime(item) {
      return toDatetime(item.date)
    },
    getNewSelection(updatedItems, value) {
      let updateKeys = updatedItems.map(i => i.key)
      let notUpdatedItems = this.selected.filter(e => !updateKeys.includes(e.key))

      // Either return new list with
      // - updated elements pushed at end
      // - updated elements filtered
      return value ? [...notUpdatedItems, ...updatedItems] : notUpdatedItems
    },
    onSelectAll(update) {
      let newSelection = this.getNewSelection(update.items, update.value)
      this.$emit("selectChange", newSelection)
    },
    onSelectChange(update) {
      let newSelection = this.getNewSelection([update.item], update.value)
      this.$emit("selectChange", newSelection)
    },
    openAI(item) {
      this.editedItem = item
      this.dialog = "DialogIA"
      this.isDialogOpen = true
    },
    openDelete(item) {
      this.editedItem = item
      this.dialog = "DialogDeleteFile"
      this.isDialogOpen = true
    },
    openRename(item) {
      this.editedItem = item
      this.dialog = "EditDocumentDialog"
      this.isDialogOpen = true
    },
    openExpire(item) {
      this.editedItem = item
      this.dialog = "EditDateDocumentDialog"
      this.isDialogOpenExpire = true
    },
    openDownload(item) {
      // Direct download, no dialog here
      this.loading = true
      if (item.file_url) {
        window.open(item.file_url, "blank")
        this.loading = false
      } else {
        let fileType
        if (item.file_type === "pcq_dsi") {
          fileType = item.file_type
        } else if (item.file_type === "cfsi") {
          fileType = item.file_type
        } else {
          fileType = item.file_type == "inspection" ? "inspections" : "non-conformities"
        }

        DocumentationService.downloadFile(
          this,
          this.projectId,
          fileType,
          this.$i18n.locale,
          item.id
        )
          .then(data => {
            const dataUrl = window.URL.createObjectURL(data)
            let filename = ""
            if (fileType == "non-conformities") {
              filename = `non-conformity-${item.name}.pdf`
            } else {
              filename = getFilename("pdf", fileType)
            }
            this.loading = false
            downloadRaw(dataUrl, filename)
          })
          .catch(error => {
            this.loading = false
            this.$store.commit("app/message", {
              msg: `${this.$t("commons.error").toUpperCase()}: ${error.status}`,
              color: "error"
            })
          })
      }
    },

    openDownloadWord(item) {
      // Direct download, no dialog here
      this.loading = true
      if (item.file_url) {
        window.open(item.file_url, "blank")
        this.loading = false
      } else {
        let fileType
        if (item.file_type === "pcq_dsi") {
          fileType = item.file_type
          PcqDsiService.wordExport(this, this.projectId)
            .then(data => {
              console.log(data)
              const blob = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              })
              const blobUrl = window.URL.createObjectURL(blob)
              const filename = getFilename("docx", fileType)
              downloadRaw(blobUrl, filename)
              this.loading = false
            })
            .catch(error => {
              this.loading = false
              this.$store.commit("app/message", {
                msg: `${this.$t("commons.error").toUpperCase()}: ${error.status}`,
                color: "error"
              })
            })
        } else {
          fileType = item.file_type == "inspection" ? "inspections" : "non-conformities"
          DocumentationService.wordExport(this, this.projectId, fileType, item.id)
            .then(data => {
              console.log(data)
              const blob = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              })
              const blobUrl = window.URL.createObjectURL(blob)
              const filename = getFilename("docx", fileType)
              downloadRaw(blobUrl, filename)
              this.loading = false
            })
            .catch(error => {
              this.loading = false
              this.$store.commit("app/message", {
                msg: `${this.$t("commons.error").toUpperCase()}: ${error.status}`,
                color: "error"
              })
            })
        }
      }
    },
    openViewer(item) {
      this.editedItem = item
      this.dialog = this.isPDF(item) ? "DialogPdfViewer" : "DialogImgViewer"
      this.isDialogOpen = true
    },
    openUpload() {
      this.$emit("upload", true)
    }
  }
}
</script>

<style lang="scss" scoped>
.placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.spin-loading {
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
